import React, { useEffect, useState } from 'react'
import { BrowserRouter as RouterModule, Navigate, Route, Routes as Switch, useLocation } from 'react-router'
import { DepotoCore } from 'depoto-core'
import { Carrier, CompanyCarrier, Order } from 'depoto-core'
import { Schema } from '../utils'
import {
  CheckoutClose,
  CheckoutClosingDetail,
  CheckoutDetail,
  CheckoutOpen,
  CheckoutsList,
  CustomerList,
  Dashboard,
  ExternalLogin,
  Login,
  LoginPin,
  NotFound404,
  OrderDetail,
  PreviewBill,
  PreviewReservation,
  ProductsAndOrderItems,
  ReservationDetail, Root,
  SecondDisplay,
  Settings
} from './'
import {Loader, SideNavigation} from '../components'
import { useCore, useLogin } from '../hooks'
import { GeoConfig } from 'depoto-core'
import { Fn } from 'depoto-core'


type Props = {
  core: DepotoCore
}

export const Router: React.FC<Props> = ({ core }) => {
  const [isReady, setIsReady] = useState(false)
  const {
    setCore,
    setCurrency,
    setCarriers,
    setCheckouts,
    selectedCheckoutId,
    setCountries,
    setCompany,
    setChildCompanies,
    setCurrencies,
    setDepots,
    setVats,
    setPayments,
    setTags,
    setIsStrictDepotFilter,
  } = useCore()
  const { isLoggedIn, lastLoginTime, handleAuthInit, user } = useLogin()

  useEffect(() => {
    if (core) {
      if (!isLoggedIn && core.services.oauth.session.token?.accessToken?.length && +new Date() - core.services.oauth.session.token?.timestamp < 24 * 60 * 60 * 1000) {
        handleAuthInit(core)
      } else if (isLoggedIn && user?.email?.length) {
        core.services.user.setCurrentUser(user.email)
      }
      setCore(core)
    }
    setIsReady(true)
    reloadTokensAndLists(
      core,
      isLoggedIn,
      setCurrency,
      setCarriers,
      setCheckouts,
      setCountries,
      setCompany,
      setChildCompanies,
      setCurrencies,
      setDepots,
      setVats,
      setPayments,
      setTags,
      setIsStrictDepotFilter,
    ).then(() => setIsReady(true))
  // }, [core, core?.services.oauth.session?.token?.timestamp, isLoggedIn, selectedCheckoutId])
  }, [core, isLoggedIn, lastLoginTime, selectedCheckoutId])

  if (!isReady) {
    return <Loader />
  }
  return (
    <RouterModule>
      <Routes core={core} />
    </RouterModule>
  )
}

const Routes: React.FC<any> = ({ core }) => {
  const location = useLocation()
  const isSideNavigationShown = location.pathname !== '/login' && location.pathname !== '/login/pin' && location.pathname !== '/second-display' && location.pathname !== '/'

  return (
    <div className="flex flex-row justify-start">
      {isSideNavigationShown && <SideNavigation />}
      <div className={!isSideNavigationShown ? 'w-full' : 'w-full-with-sidenav'}>
        <RootRedirect />
        <Switch location={location}>
          <Route path="/" element={<Root />} />
          <Route path="/login" element={<Login core={core} />} />
          <Route path="/login/pin" element={<LoginPin core={core} />} />
          <Route path="/external-login" element={<ExternalLogin core={core} />} />
          <Route path="/checkout-select" element={<CheckoutsList />} />
          <Route path="/checkout-open/:id" element={<CheckoutOpen />} />
          <Route path="/checkout-close/:id" element={<CheckoutClose />} />
          <Route path="/dashboard" element={<ProductsAndOrderItems />} />
          <Route path="/preview-reservation" element={<PreviewReservation />} />
          <Route path="/preview-bill" element={<PreviewBill />} />
          <Route path="/customer-list" element={<CustomerList />} />
          <Route path="/checkout-detail" element={<CheckoutDetail />} />
          <Route path="/checkout-detail/:tab" element={<CheckoutDetail />} />
          <Route path="/checkout-detail/order/:id" element={<OrderDetail />} />
          <Route path="/checkout-detail/reservation/:id" element={<ReservationDetail />} />
          <Route path="/checkout-detail/checkout-closing/:id" element={<CheckoutClosingDetail />} />
          <Route path="/second-display" element={<SecondDisplay />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<NotFound404 />} />
        </Switch>
      </div>
    </div>
  )
}

const RootRedirect = () => {
  const { isLoggedIn } = useLogin()
  const location: any = useLocation()
  if (location.pathname === '/') {
    return (
      <Navigate
        to={{
          pathname: isLoggedIn ? '/dashboard' : '/login',
        }}
        state={{ from: location }}
      />
    )
  }
  return null
}

const reloadTokensAndLists = async (
  core: DepotoCore,
  isLoggedIn: boolean,
  setCurrency: Fn,
  setCarriers: Fn,
  setCheckouts: Fn,
  setCountries: Fn,
  setCompany: Fn,
  setChildCompanies: Fn,
  setCurrencies: Fn,
  setDepots: Fn,
  setVats: Fn,
  setPayments: Fn,
  setTags: Fn,
  setIsStrictDepotFilter: Fn,
): Promise<void> => {
  const filters: any = {}
  core.services.oauth.onLogoutEmitter
    .subscribe()
    .then(() => (window.location.pathname !== '/login' && window.location.pathname !== '/external-login' ? (window.location.pathname = '/login') : null))
  core.services.oauth.session.clientId = '36_27y4bbpod2m8skgk4scwsc8gcw4c044s40gcw44swcc4s0s4kk'
  core.services.oauth.session.clientSecret = '53gxznakf44kowsgs8ww4csk8080k8wkk0s84kcswk80c48og0'
  if (window.depotoEnv === 'custom' && window.localStorage.getItem('depoto_server_custom_url')?.length) {
    core.services.oauth.session.clientCustomUri = `${window.localStorage.getItem('depoto_server_custom_url') || ''}`
    core.services.oauth.session.clientType = 'custom'
  }
  if (isLoggedIn) {
    const doesTokenExists = core.services.oauth?.session?.token?.accessToken?.length > 0
    const user = doesTokenExists ? await core.services.user.getMyProfile(Schema.user.detail) : null
    if (!user) {
      return core.services.oauth.logout()
    }
    const [company, childCompanies] = [user.company, user?.company?.children || []]
    const [carriers, checkouts, countries, currencies, depots, vats, payments, tags] = await Promise.all([
      core.services.companyCarrier
        .getList({ filters }, Schema.companyCarrier.list) // todo on parent company there is nothing even with filter... (TN knows and will do smth about it)
        .then(r => [
          { id: undefined, name: ' ' },
          ...r.items
            .map((c: CompanyCarrier) => c.carrier)
            .sort((a: Carrier, b: Carrier) => (a.position === b.position ? 0 : a.position > b.position ? 1 : -1)),
        ]),
      // core.services.carrier.getList().then(r => [{id: undefined, name: ' '}, ...r.items.sort((a: Carrier, b: Carrier) => a.position === b.position ? 0 : a.position > b.position ? 1 : -1)] || []),
      core.services.checkout.getList({ filters }, Schema.checkout.detail).then(r => r.items || []),
      GeoConfig.countries,
      core.services.payment.getCurrencies(undefined, Schema.currency.detail).then(r => r.items || []),
      core.services.depot.getList({ filters }, Schema.basic.list).then(r => r.items || []),
      core.services.vat.getList({ filters }, Schema.vat.list).then(r => r.items || []),
      core.services.payment.getList({ filters }, Schema.payment.list).then(r => r.items || []),
      core.services.tag
        .getList({ filters }, { ...Schema.basic.list, type: null, externalId: null })
        .then(r => r.items || []),
    ])
    const uniqueCarrierIds = Array.from(new Set(carriers.map(c => c.id)))
    const uniqueCarriers = []
    for (const cId of uniqueCarrierIds) {
      uniqueCarriers.push(carriers.find(c => c.id === cId))
    }
    const isStrictDepotFilter = window.localStorage.getItem('isStrictDepotFilter') === 'true'
    const currency = {
      ...company.currency,
      symbol: company.currency.id === 'EUR' ? '€' : company.currency.id === 'CZK' ? 'Kč' : company.currency.id === 'USD' ? '$' : 'Kč'
    }
    let filteredCheckouts = [...checkouts].filter(c => c.enabled)
    if (user?.checkouts?.length) {
      const cIds = user.checkouts.map(c => c.id)
      filteredCheckouts = checkouts.filter(c => cIds.includes(c.id))
    }
    setCurrency(currency)
    setCarriers(uniqueCarriers)
    setCheckouts(filteredCheckouts)
    setCountries(countries)
    setCompany(company)
    setChildCompanies(childCompanies)
    setCurrencies(currencies)
    setDepots(depots)
    setVats(vats)
    setPayments(payments)
    setTags(tags)
    setIsStrictDepotFilter(isStrictDepotFilter)
  } else {
    core.services.oauth.logout()
  }
}

export const setSearchParams = (params: { [key: string]: string | number } | any) => {
  let queryStr = '?'
  Object.keys(params).forEach((k, i) => {
    // console.warn(k, params[k], i)
    if (i > 0) {
      queryStr += '&'
    }
    if (params[k] !== undefined && params[k] !== 'all') {
      queryStr += `${k}=${params[k]}`
    }
  })
  // console.warn(params, queryStr)
  const oldLocation = `${window.location.origin}${window.location.pathname}`
  history.pushState(null, '', `${oldLocation}${queryStr === '?' ? '' : queryStr}`)
}

export const useSearchParams = () => {
  const queryString = window.location.search ? window.location.search : ''
  const spArr = queryString.replace('?', '').split('&')
  const searchParams: any = {}
  for (const p of spArr) {
    const par = p.split('=')
    searchParams[par[0]] = par[1]
  }
  return searchParams
}
